// Qué contenidos lleva un informe: la lógica de la selección, fuera del
// formulario. Es la única parte de esta pantalla que puede estar mal sin que
// nada se vea roto —un ítem que se cae de la lista, o un orden distinto del de
// la plantilla— y dentro del árbol un test no la distinguiría del render.
//
// Las reglas:
// - La plantilla manda el ORDEN (el de sus filas, no el de los ids).
// - Los OBLIGATORIOS nacen marcados y SE PUEDEN QUITAR: la plantilla propone,
//   quien arma el informe decide. Siguen rotulados como obligatorios para que
//   quitarlos sea una decisión a la vista y no un descuido.
// - Condicionales y opcionales nacen sin marcar: los elige quien arma el
//   informe.

import type { Informe } from "./informes";
import type { ContenidoDeLaPlantilla } from "./plantillas";

/** Lo mínimo que hace falta para ubicar un contenido en el árbol. */
interface Ubicable {
  grupo: string;
  subgrupo: string | null;
}

export interface SubgrupoDeContenidos<T extends Ubicable = ContenidoDeLaPlantilla> {
  subgrupo: string | null;
  items: T[];
}

export interface GrupoDeContenidos<T extends Ubicable = ContenidoDeLaPlantilla> {
  grupo: string;
  subgrupos: SubgrupoDeContenidos<T>[];
  /** Todos los ítems del grupo, en orden. */
  items: T[];
}

/**
 * Arma Grupo › Subgrupo › Ítem respetando el orden en que llegan. Un grupo (o
 * subgrupo) que reaparece más adelante se junta con el anterior en vez de
 * duplicarse: la plantilla los guarda contiguos, pero esto no depende de eso.
 */
export function arbolDeContenidos<T extends Ubicable>(contenidos: T[]): GrupoDeContenidos<T>[] {
  const grupos: GrupoDeContenidos<T>[] = [];

  for (const contenido of contenidos) {
    let grupo = grupos.find((g) => g.grupo === contenido.grupo);
    if (!grupo) {
      grupo = { grupo: contenido.grupo, subgrupos: [], items: [] };
      grupos.push(grupo);
    }

    let subgrupo = grupo.subgrupos.find((s) => s.subgrupo === contenido.subgrupo);
    if (!subgrupo) {
      subgrupo = { subgrupo: contenido.subgrupo, items: [] };
      grupo.subgrupos.push(subgrupo);
    }

    subgrupo.items.push(contenido);
    grupo.items.push(contenido);
  }

  return grupos;
}

export function idsDeLosObligatorios(contenidos: ContenidoDeLaPlantilla[]): number[] {
  return contenidos.filter((c) => c.obligatoriedad === "obligatorio").map((c) => c.maestro_item_id);
}

/** Con qué arranca un informe que acaba de elegir plantilla. */
export function seleccionInicial(contenidos: ContenidoDeLaPlantilla[]): number[] {
  return idsDeLosObligatorios(contenidos);
}

/**
 * Deja la selección como la plantilla la admite: sin ítems ajenos y en el
 * orden de la plantilla.
 */
export function normalizarSeleccion(
  contenidos: ContenidoDeLaPlantilla[],
  seleccion: number[],
): number[] {
  const elegidos = new Set(seleccion);
  return contenidos
    .filter((c) => elegidos.has(c.maestro_item_id))
    .map((c) => c.maestro_item_id);
}

/** Lo que viaja al servidor: la selección normalizada. */
export function itemsParaEnviar(
  contenidos: ContenidoDeLaPlantilla[],
  seleccion: number[],
): number[] {
  return normalizarSeleccion(contenidos, seleccion);
}

export function alternarItem(
  contenidos: ContenidoDeLaPlantilla[],
  seleccion: number[],
  id: number,
  marcado: boolean,
): number[] {
  const siguiente = marcado ? [...seleccion, id] : seleccion.filter((x) => x !== id);
  return normalizarSeleccion(contenidos, siguiente);
}

export type EstadoDelConjunto = "todos" | "ninguno" | "algunos";

/** El estado de la casilla de un grupo o subgrupo (tres estados). */
export function estadoDelConjunto(
  items: ContenidoDeLaPlantilla[],
  seleccion: number[],
): EstadoDelConjunto {
  const elegidos = new Set(seleccion);
  const marcados = items.filter((i) => elegidos.has(i.maestro_item_id)).length;
  if (marcados === 0) return "ninguno";
  return marcados === items.length ? "todos" : "algunos";
}

/** Marcar o desmarcar un grupo entero, obligatorios incluidos. */
export function alternarConjunto(
  contenidos: ContenidoDeLaPlantilla[],
  seleccion: number[],
  items: ContenidoDeLaPlantilla[],
  marcar: boolean,
): number[] {
  const ids = new Set(items.map((i) => i.maestro_item_id));
  const siguiente = marcar
    ? [...seleccion, ...ids]
    : seleccion.filter((id) => !ids.has(id));
  return normalizarSeleccion(contenidos, siguiente);
}

/**
 * ¿La persona tocó la selección? Se usa para confirmar antes de cambiar de
 * plantilla: reiniciarla en silencio tiraría una elección hecha a mano.
 */
export function seleccionCambiada(
  contenidos: ContenidoDeLaPlantilla[],
  seleccion: number[],
): boolean {
  const actual = normalizarSeleccion(contenidos, seleccion);
  const inicial = seleccionInicial(contenidos);
  return actual.length !== inicial.length || actual.some((id, i) => id !== inicial[i]);
}

/**
 * ¿El informe tiene contenidos definidos? Mira los ítems de su plantilla y, si
 * no hay, la lista plana de los informes anteriores a las plantillas: esos
 * tienen su información y no hay motivo para marcarlos como incompletos.
 */
export function informeTieneContenidos(informe: Pick<Informe, "items" | "contenidos">): boolean {
  return (informe.items?.length ?? 0) > 0 || (informe.contenidos?.length ?? 0) > 0;
}
